// src/pages/Updates/index.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Input,
  Spinner,
  Alert,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { getUpdates } from "../../helpers/eirna_backend_helper";
import { FadeLoader, HashLoader } from "react-spinners";

const Updates = () => {
  // State management
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch updates on component mount
  useEffect(() => {
    fetchUpdates();
  }, []);

  const fetchUpdates = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await getUpdates();

      if (response.status === "success") {
        // Sort by row_id in descending order (newest first)
        const sortedUpdates = response.update_list.sort(
          (a, b) => b.row_id - a.row_id
        );
        setUpdates(sortedUpdates);
      } else {
        setError("Failed to fetch updates");
      }
    } catch (err) {
      setError("Failed to load updates. Please try again later.");
      console.error("Error fetching updates:", err);
    } finally {
      setLoading(false);
    }
  };

  // Filter updates based on search term
  const filteredUpdates = useMemo(() => {
    if (!searchTerm) return updates;

    return updates.filter(
      (update) =>
        update.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        update.update_type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        update.date.includes(searchTerm)
    );
  }, [updates, searchTerm]);

  // Pagination logic
  const totalPages = Math.ceil(filteredUpdates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUpdates = filteredUpdates.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const [day, month, year] = dateString.split("-");
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get badge info for update type
  const getBadgeInfo = (type) => {
    switch (type.toLowerCase()) {
      case "feature":
        return {
          color: "#10B981",
          icon: "ri-add-circle-line",
          text: "Feature",
        };
      case "bug fix":
        return {
          color: "#F59E0B",
          icon: "ri-bug-line",
          text: "Bug Fix",
        };
      case "misc":
        return {
          color: "#3B82F6",
          icon: "ri-information-line",
          text: "Misc",
        };
      default:
        return {
          color: "#6B7280",
          icon: "ri-folder-line",
          text: type,
        };
    }
  };

  // Truncate description
  const truncateText = (text, maxLength = 120) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  // Render pagination
  const renderPagination = () => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <PaginationItem key="prev" disabled={currentPage === 1}>
        <PaginationLink
          previous
          onClick={() => handlePageChange(currentPage - 1)}
        >
          <i className="ri-arrow-left-line"></i>
        </PaginationLink>
      </PaginationItem>
    );

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem key={i} active={i === currentPage}>
          <PaginationLink onClick={() => handlePageChange(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>
      );
    }

    // Next button
    pages.push(
      <PaginationItem key="next" disabled={currentPage === totalPages}>
        <PaginationLink next onClick={() => handlePageChange(currentPage + 1)}>
          <i className="ri-arrow-right-line"></i>
        </PaginationLink>
      </PaginationItem>
    );

    return pages;
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#1A6D52" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "Updates | EIRNABio Connect";

  return (
    <div className="page-content">
      <Container fluid>
        {/* Page Header */}
        <Row className="mb-4">
          <Col lg={12}>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0 fw-bold" style={{ color: "#374151" }}>
                  {" "}
                  Updates
                </h4>
                <p className="mb-0 text-muted" style={{ fontSize: "13px" }}>
                  Track all platform updates and improvements
                </p>
              </div>

              {/* Search Input */}
              <div className="position-relative" style={{ width: "300px" }}>
                <Input
                  type="text"
                  placeholder="Search updates..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{
                    paddingLeft: "40px",
                    height: "38px",
                    fontSize: "13px",
                  }}
                />
                <i
                  className="ri-search-line position-absolute"
                  style={{
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#9CA3AF",
                    fontSize: "16px",
                  }}
                ></i>
              </div>
            </div>
          </Col>
        </Row>

        {/* Main Content */}
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                {/* Loading State */}

                {/* Error State */}
                {error && (
                  <Alert color="danger" className="mb-0">
                    <i className="ri-error-warning-line me-2"></i>
                    {error}
                  </Alert>
                )}

                {/* Table */}
                {!loading && !error && (
                  <>
                    <div className="table-responsive">
                      <Table className="table-hover table-bordered table-striped align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            <th
                              scope="col"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#2F9E37",
                              }}
                            >
                              Number
                            </th>
                            <th
                              scope="col"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#2F9E37",
                              }}
                            >
                              Date
                            </th>
                            <th
                              scope="col"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#2F9E37",
                              }}
                            >
                              Type
                            </th>
                            <th
                              scope="col"
                              style={{
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#2F9E37",
                              }}
                            >
                              Description
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentUpdates.length > 0 ? (
                            currentUpdates.map((update) => {
                              const badgeInfo = getBadgeInfo(
                                update.update_type
                              );
                              return (
                                <tr key={update.row_id}>
                                  <td
                                    className="fw-medium"
                                    style={{ fontSize: "13px" }}
                                  >
                                    {update.row_id}
                                  </td>
                                  <td style={{ fontSize: "13px" }}>
                                    {formatDate(update.date)}
                                  </td>
                                  <td>
                                    <i
                                      className={`${badgeInfo.icon} align-middle me-1`}
                                      style={{
                                        color: badgeInfo.color,
                                        fontSize: "14px",
                                      }}
                                    ></i>
                                    <span style={{ fontSize: "13px" }}>
                                      {badgeInfo.text}
                                    </span>
                                  </td>
                                  <td
                                    style={{ fontSize: "13px" }}
                                    title={update.description}
                                  >
                                    {truncateText(update.description)}
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="4" className="text-center py-4">
                                <div className="text-muted">
                                  <i
                                    className="ri-inbox-line"
                                    style={{ fontSize: "32px" }}
                                  ></i>
                                  <p
                                    className="mb-0 mt-2"
                                    style={{ fontSize: "14px" }}
                                  >
                                    {searchTerm
                                      ? "No updates found matching your search."
                                      : "No updates available."}
                                  </p>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Footer with pagination */}
                    {filteredUpdates.length > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div>
                          <span
                            className="text-muted"
                            style={{ fontSize: "13px" }}
                          >
                            Showing {startIndex + 1} to{" "}
                            {Math.min(endIndex, filteredUpdates.length)} of{" "}
                            {filteredUpdates.length} entries
                          </span>
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && (
                          <Pagination className="mb-0" size="sm">
                            {renderPagination()}
                          </Pagination>
                        )}
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Updates;
